import { Dispatch, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import * as R from 'ramda';

import {
  SubItemTreeAction,
  SubItemTreeActionType,
} from '@atom/components/common/subItemTree/subItemTreeReducer';
import { GET_ASSET_CHANGE_TYPES } from '@atom/graph/asset';
import { Snackbar } from '@atom/mui';
import { ChangeType } from '@atom/types/asset';

type UseChangeTypeRefreshInput = {
  rootAssetId: string;
  dispatch: Dispatch<SubItemTreeAction>;
};

type UseChangeTypeRefreshValue = {
  refreshChangeTypes: (assetIds: string[]) => Promise<ChangeType | null>;
};

const useChangeTypeRefresh = ({
  rootAssetId,
  dispatch,
}: UseChangeTypeRefreshInput): UseChangeTypeRefreshValue => {
  const client = useApolloClient();

  const refreshChangeTypes = useCallback(
    async (nodeIds: string[]): Promise<ChangeType | null> => {
      const assetIds = R.uniq([rootAssetId, ...nodeIds]);

      try {
        const { data } = await client.query({
          query: GET_ASSET_CHANGE_TYPES,
          variables: { input: { rootAssetId, assetIds } },
          fetchPolicy: 'no-cache',
        });
        const changeStates = data.assetChangeTypes;

        dispatch({
          type: SubItemTreeActionType.SET_CHANGE_TYPES,
          data: changeStates,
        });

        return (
          changeStates.find(state => state.assetId === rootAssetId)
            ?.changeType ?? null
        );
      } catch (err) {
        Snackbar.error({ message: 'An error occurred. Please try again.' });
        return null;
      }
    },
    [client, dispatch, rootAssetId],
  );

  return { refreshChangeTypes };
};

export default useChangeTypeRefresh;
